import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import CustomInput from '@/components/CustomInput/CustomInput';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import {
  Alert,
  Box,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { OrganizationsApi } from '../../api/clients/organizations/organizations.api';
import {
  isValidHttpUrl,
  normalizeClientWebsiteLabel,
  normalizeClientWebsiteUrl,
} from '@/utils/url.util';

const getClientTransactionIdDuplicateGroups = (error) => {
  return (
    error.response?.data?.errors?.flatMap(
      (item) => item.duplicate_groups || [],
    ) || []
  );
};

const OrganizationFeatures = ({ orgId, features, setFeatures }) => {
  const [
    clientTransactionIdDuplicateGroups,
    setClientTransactionIdDuplicateGroups,
  ] = useState([]);
  const [clientWebsiteUrlError, setClientWebsiteUrlError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await OrganizationsApi.getOrganizationFeatures(orgId);
        const {
          showApprovalsPage = false,
          showCreatePaymentPage = false,
          showBusinessUnitsPage = false,
          showFacilityManagementPage = false,
          showSupplierLookupModal = false,
          showBulkApproveDecline = true,
          enforceClientTransactionIdUniqueness = false,
          disableLegacyEmailTemplates = false,
          clientWebsiteUrl,
          clientWebsiteLabel,
        } = res || {};

        setFeatures({
          showApprovalsPage,
          showCreatePaymentPage,
          showBusinessUnitsPage,
          showFacilityManagementPage,
          showSupplierLookupModal,
          showBulkApproveDecline,
          enforceClientTransactionIdUniqueness,
          disableLegacyEmailTemplates,
          clientWebsiteUrl: normalizeClientWebsiteUrl(clientWebsiteUrl),
          clientWebsiteLabel: normalizeClientWebsiteLabel(clientWebsiteLabel),
        });
        setClientWebsiteUrlError('');
        setClientTransactionIdDuplicateGroups([]);
      } catch (error) {
        console.error('Error fetching organization features:', error);
        toast.error('Failed to load organization features');
      }
    };
    if (orgId) {
      fetchData();
    }
  }, [orgId, setFeatures]);

  const updateOrganizationFeatures = async (value, key) => {
    const previousFeatures = { ...features };

    try {
      setClientTransactionIdDuplicateGroups([]);
      const normalizedValue =
        key === 'clientWebsiteLabel'
          ? normalizeClientWebsiteLabel(value)
          : value;
      const newFeatures = { ...features, [key]: normalizedValue };
      const trimmedUrl = normalizeClientWebsiteUrl(
        newFeatures.clientWebsiteUrl,
      );
      const urlIsValid = !trimmedUrl || isValidHttpUrl(trimmedUrl);
      const isLabelOnlyUpdate = key === 'clientWebsiteLabel';

      if (!isLabelOnlyUpdate && !validateClientWebsiteUrl(trimmedUrl)) {
        return;
      }

      if (isLabelOnlyUpdate && trimmedUrl && !urlIsValid) {
        setClientWebsiteUrlError('Please enter a valid HTTP/HTTPS URL');
      }

      newFeatures.clientWebsiteUrl = trimmedUrl;
      setFeatures(newFeatures);

      if (orgId) {
        const payload = {
          ...newFeatures,
          clientWebsiteLabel:
            normalizeClientWebsiteLabel(newFeatures.clientWebsiteLabel) || null,
        };

        if (isLabelOnlyUpdate && !urlIsValid) {
          delete payload.clientWebsiteUrl;
        } else {
          payload.clientWebsiteUrl = trimmedUrl || null;
        }

        await OrganizationsApi.updateOrganizationFeatures(orgId, payload);
        toast.success('Feature updated successfully');
      }
    } catch (error) {
      console.error('Error updating organization features:', error);
      const duplicateGroups = getClientTransactionIdDuplicateGroups(error);
      setClientTransactionIdDuplicateGroups(duplicateGroups);
      toast.error(
        duplicateGroups.length > 0
          ? 'Resolve duplicate Client Transaction IDs before enabling this feature'
          : 'Failed to update feature',
      );
      setFeatures(previousFeatures);
    }
  };

  const validateClientWebsiteUrl = (url) => {
    if (url && !isValidHttpUrl(url)) {
      setClientWebsiteUrlError('Please enter a valid HTTP/HTTPS URL');
      return false;
    }
    setClientWebsiteUrlError('');
    return true;
  };

  const handleClientWebsiteUrlChange = (e) => {
    const value = e.target.value;
    setClientWebsiteUrlError('');
    setFeatures((prev) => ({ ...prev, clientWebsiteUrl: value }));
  };

  const handleClientWebsiteUrlBlur = () => {
    const trimmed = features.clientWebsiteUrl?.trim() || '';
    setFeatures((prev) => ({ ...prev, clientWebsiteUrl: trimmed }));
    validateClientWebsiteUrl(trimmed);
  };

  const handleClientWebsiteLabelChange = (e) => {
    setFeatures((prev) => ({ ...prev, clientWebsiteLabel: e.target.value }));
  };

  const handleClientWebsiteLabelBlur = () => {
    const trimmed = normalizeClientWebsiteLabel(features.clientWebsiteLabel);
    setFeatures((prev) => ({ ...prev, clientWebsiteLabel: trimmed }));
    if (orgId) {
      updateOrganizationFeatures(trimmed, 'clientWebsiteLabel');
    }
  };

  return (
    <GridContainer
      style={{ paddingTop: '30px', display: 'flex', flexDirection: 'column' }}>
      <GridItem xs={12} sm={12} md={12}>
        <h4>
          <b>Features</b>
        </h4>
        <br />
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={features.showApprovalsPage}
              onChange={(e) =>
                updateOrganizationFeatures(
                  e.target.checked,
                  'showApprovalsPage',
                )
              }
              name='automated_ach_return_enabled'
              color='primary'
            />
          }
          label='Enable Approvals Page'
        />
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={features.showCreatePaymentPage}
              onChange={(e) =>
                updateOrganizationFeatures(
                  e.target.checked,
                  'showCreatePaymentPage',
                )
              }
              name='automated_ach_return_enabled'
              color='primary'
            />
          }
          label='Enable Create Payment Page'
        />
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={features.showBusinessUnitsPage}
              onChange={(e) =>
                updateOrganizationFeatures(
                  e.target.checked,
                  'showBusinessUnitsPage',
                )
              }
              name='automated_ach_return_enabled'
              color='primary'
            />
          }
          label='Enable Business Units Page'
        />
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={features.showFacilityManagementPage}
              onChange={(e) =>
                updateOrganizationFeatures(
                  e.target.checked,
                  'showFacilityManagementPage',
                )
              }
              name='show_facility_management_page'
              color='primary'
            />
          }
          label='Enable Facility User Management'
        />
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={features.showSupplierLookupModal}
              onChange={(e) =>
                updateOrganizationFeatures(
                  e.target.checked,
                  'showSupplierLookupModal',
                )
              }
              name='show_supplier_lookup_modal'
              color='primary'
            />
          }
          label='Enable Supplier Lookup Modal'
        />
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={features.disableLegacyEmailTemplates}
              onChange={(e) =>
                updateOrganizationFeatures(
                  e.target.checked,
                  'disableLegacyEmailTemplates',
                )
              }
              name='disable_legacy_email_templates'
              color='primary'
            />
          }
          label='Disable Legacy Email Templates'
        />
      </GridItem>

      <GridItem xs={12} sm={12} md={6}>
        <CustomInput
          labelText='Client Portal URL'
          placeholder='https://example.com'
          type='text'
          id='clientWebsiteUrl'
          name='clientWebsiteUrl'
          value={features.clientWebsiteUrl || ''}
          onChange={handleClientWebsiteUrlChange}
          error={!!clientWebsiteUrlError}
          formControlProps={{ fullWidth: true }}
          inputProps={{
            onBlur: handleClientWebsiteUrlBlur,
            helperText: clientWebsiteUrlError,
          }}
        />
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <CustomInput
          labelText='Client Portal display label'
          placeholder='Supplier Portal'
          type='text'
          id='clientWebsiteLabel'
          name='clientWebsiteLabel'
          value={normalizeClientWebsiteLabel(features.clientWebsiteLabel)}
          onChange={handleClientWebsiteLabelChange}
          formControlProps={{ fullWidth: true }}
          inputProps={{
            onBlur: handleClientWebsiteLabelBlur,
            helperText:
              'Optional label shown on Create Payment. Defaults to Client website when empty.',
          }}
        />
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={features.showBulkApproveDecline}
              onChange={(e) =>
                updateOrganizationFeatures(
                  e.target.checked,
                  'showBulkApproveDecline',
                )
              }
              name='show_bulk_approve_decline'
              color='primary'
            />
          }
          label='Enable Bulk Approve/Decline'
        />
      </GridItem>
      <GridItem xs={12} sm={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={!!features.enforceClientTransactionIdUniqueness}
              onChange={(e) =>
                updateOrganizationFeatures(
                  e.target.checked,
                  'enforceClientTransactionIdUniqueness',
                )
              }
              name='enforce_client_transaction_id_uniqueness'
              color='primary'
            />
          }
          label='Enforce Client Transaction ID uniqueness'
        />
        <Alert severity='info' sx={{ mt: 1, mb: 2 }}>
          <Typography variant='body2'>
            Enabling requires no existing duplicate Client Transaction IDs among
            non-deleted Payments.
          </Typography>
        </Alert>
      </GridItem>
      {clientTransactionIdDuplicateGroups.length > 0 && (
        <GridItem xs={12} sm={12} md={12}>
          <Alert
            severity='error'
            icon={<ReportProblemOutlinedIcon sx={{ color: 'error.main' }} />}
            sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant='body2' sx={{ fontWeight: 600 }}>
                Duplicate Client Transaction IDs must be cleaned up first.
              </Typography>

              {clientTransactionIdDuplicateGroups.map((group) => (
                <Box
                  key={group.client_transaction_id}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.55)',
                    border: '1px solid',
                    borderColor: 'error.light',
                    borderRadius: 1,
                    p: 1.25,
                  }}>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    {group.client_transaction_id}
                  </Typography>
                  <Typography variant='body2' sx={{ wordBreak: 'break-word' }}>
                    {group.count} payments; IDs: {group.payment_ids.join(', ')}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Alert>
        </GridItem>
      )}
    </GridContainer>
  );
};

export default OrganizationFeatures;
